import React from "react"
import { ActionDialog, Button, DatePicker, TextField, Typography, icons, theme } from "cdh-data-portal-theme"
import { useAssignLineManagerDeputy } from "./AssignLineManagerDeputy.logic"
import { useStyles } from "./AssignLineManagerDeputy.style"
import { localtext, type NewLineManagerAssignment } from "./AssignLineManagerDeputy.static"

export interface AssignLineManagerDeputyProps {
  readonly className?: string
  readonly onAssignmentAdded: (assignment: NewLineManagerAssignment) => void
}

export const AssignLineManagerDeputy: React.FC<AssignLineManagerDeputyProps> = ({ className, onAssignmentAdded }) => {
  const { classes, cx } = useStyles()
  const {
    closeConfirmation,
    confirmAssignment,
    coverageDuration,
    currentLineManager,
    endDate,
    errors,
    handleSubmit,
    isConfirmationOpen,
    maxEndDate,
    setCurrentLineManager,
    setEndDate,
    setStartingDate,
    setTemporaryDeputy,
    setTicketUrl,
    startingDate,
    temporaryDeputy,
    ticketUrl,
  } = useAssignLineManagerDeputy(onAssignmentAdded)

  return (
    <div className={cx(classes.root, className)}>
      <div className={classes.header}>
        <Typography variant="headline2" color={theme.textColors.primary}>{localtext.Title}</Typography>
      </div>
      <div className={classes.body}>
        <TextField
          label={localtext.CurrentLineManager}
          placeholder={localtext.PlaceholderEmployee}
          errorText={errors.currentLineManager}
          value={currentLineManager}
          onChange={event => setCurrentLineManager(event.target.value)}
        />
        <TextField
          label={localtext.TemporaryDeputy}
          placeholder={localtext.PlaceholderEmployee}
          errorText={errors.temporaryDeputy}
          value={temporaryDeputy}
          onChange={event => setTemporaryDeputy(event.target.value)}
        />
        <div className={classes.dateRow}>
          <DatePicker
            showDatePickerOnRender
            className={classes.datePicker}
            label={localtext.StartingDate}
            placeholder={localtext.PlaceholderStartDate}
            errorText={errors.startingDate}
            value={startingDate}
            onChange={setStartingDate}
          />
          <DatePicker
            showDatePickerOnRender
            className={classes.datePicker}
            label={localtext.EndDate}
            placeholder={localtext.PlaceholderEndDate}
            errorText={errors.endDate}
            value={endDate}
            maxDate={maxEndDate}
            onChange={setEndDate}
          />
        </div>
        {coverageDuration && (
          <Typography className={classes.coverageDuration} variant="body2">
            {localtext.CoverageDuration(coverageDuration)}
          </Typography>
        )}
        <TextField
          label={localtext.TicketUrl}
          placeholder={localtext.PlaceholderTicketUrl}
          errorText={errors.ticketUrl}
          value={ticketUrl}
          onChange={e => setTicketUrl(e.target.value)}
        />
      </div>
      <div className={classes.footer}>
        <Button
          title={localtext.SubmitAssignment}
          variant="primary"
          icon={icons.userArrowUp}
          onClick={handleSubmit}
        />
      </div>
      <ActionDialog
        actionText={localtext.ApprovalConfirmationAction}
        isOpen={isConfirmationOpen}
        title={localtext.ApprovalConfirmationTitle}
        warning={{
          warningMessage: localtext.ApprovalConfirmationWarning,
          warningTitle: localtext.ApprovalConfirmationWarningTitle,
        }}
        onAccept={confirmAssignment}
        onCancel={closeConfirmation}
      >
        <Typography variant="body">{localtext.ApprovalConfirmationDescription(currentLineManager, ticketUrl)}</Typography>
      </ActionDialog>
    </div>
  )
}
